/**
 * Data model for the Net Assets chart (rework: ONE stacked bar per month,
 * exactly two segments -- Fixed Net Assets + Current Net Assets, summing to
 * the total net assets figure. No separate Liabilities bar, no Net Assets
 * line overlay; liabilities are netted INTO the two buckets on the backend
 * instead), shared between the Overall dashboard (group scope), the
 * entity/Personal dashboard, and the Assets & Liabilities page
 * (components/dashboard/NetAssetsChart.jsx is the single chart component
 * all five surfaces render through).
 *
 * Historical actuals come straight from GET /al/net-assets-series called
 * with calendar_year=true (services/net_assets_series.py) -- the canonical
 * series whose LATEST real point reconciles to compute_balance_sheet (the
 * same predicate the Net Assets stat card and the Assets & Liabilities page
 * use) by construction. Per-component historical basis (cash reconstructed,
 * fixed assets/liabilities from their own dated valuations, receivables/
 * intercompany held flat) is documented in that module and surfaced here via
 * `basis`. calendar_year mode always returns 12 months (Jan-Dec); a month
 * with no coverage (past gap, or any month after today) comes back with
 * every figure `null` -- this MUST stay null through this file, never
 * coerced to 0 (a fabricated dip/flat-line is exactly the bug class the
 * source module's own docstring calls out).
 *
 * fixed_net/current_net are backend-computed (fixed_assets - fixed_liabilities,
 * current_assets - current_liabilities), never re-derived here; by
 * construction fixed_net + current_net == net for every point.
 *
 * The endpoint has no forecast of its own. The existing 3-month forecast
 * continuation reuses the SAME cash-forecast figures the Bank Balance
 * chart's own scope uses (never a new derivation): fixed_net is held flat at
 * its latest actual value (property values and mortgages have no
 * forward-looking source), while current_net (which contains cash) rolls
 * forward using the confirmed-forward-booking floor. In calendar-year mode
 * this OVERLAYS onto the existing null future placeholder for that month
 * (never appends a duplicate month); trailing-window callers (no future
 * months in `series`) still get the forecast rows appended, as before.
 */

import { monthKeyToLabel } from './forecastChartUtils';

function toNumOrNull(v) {
  return v == null ? null : (parseFloat(v) || 0);
}

/**
 * Map one raw /al/net-assets-series point to the chart's fields.
 * Legacy fallback: a series with `net` but no `fixed_net`/`current_net`
 * (the pre-split shape, e.g. the A&L page's legacy getMonthlySeries
 * fallback when net-assets-series itself fails) buckets the whole figure as
 * Current Net Assets rather than fabricate a split that doesn't exist --
 * documented degraded mode, not silently wrong.
 */
export function compositionFieldsFromSeriesPoint(m) {
  var net = toNumOrNull(m.net);
  var fixedNet;
  var currentNet;
  if (m.fixed_net == null && m.current_net == null && net != null) {
    fixedNet = 0;
    currentNet = net;
  } else {
    fixedNet = toNumOrNull(m.fixed_net);
    currentNet = toNumOrNull(m.current_net);
  }
  return { fixedNet: fixedNet, currentNet: currentNet, net: net };
}

/**
 * The Fixed+Current stack-total label value for one chart row (== net
 * assets, by construction), or null if nothing should be labelled (a gap
 * month, or a genuine zero total).
 *
 * Gated on the ROW (fixedNet/currentNet nulled together on a gap month),
 * never on currentNet's own value alone -- currentNet can legitimately be
 * exactly 0 for a month that still has a nonzero fixedNet total, and gating
 * on currentNet's own value would wrongly suppress the stack-total label in
 * that case.
 */
export function stackTotalLabel(row) {
  if (!row || (row.fixedNet == null && row.currentNet == null)) return null;
  var total = (row.fixedNet || 0) + (row.currentNet || 0);
  return total === 0 ? null : total;
}

/**
 * @param {object} input
 * @param {object[]} input.series - GET /al/net-assets-series `series` (oldest first)
 * @param {number} input.currentCash - current total cash, SAME currency as series (the Bank Balance card figure)
 * @param {object[]} [input.cashForecastRows] - this scope's cash-forecast rows (entries with _isForecast + _forecastTotal), same currency
 */
export function buildNetAssetsChartData(input) {
  var series = input.series || [];
  var currentCash = input.currentCash || 0;

  var actualRows = series.map(function (m) {
    var fields = compositionFieldsFromSeriesPoint(m);
    return {
      month: monthKeyToLabel(m.month),
      _monthKey: m.month,
      fixedNet: fields.fixedNet,
      currentNet: fields.currentNet,
      net: fields.net,
      _isForecast: false,
      _source: m.source,
      // source="partial" (services/net_assets_series.py): some but not all
      // of today's live in-scope accounts had a cash snapshot that month --
      // the figure is real but understated, never fabricated -- so the
      // chart must flag it rather than render it identically to a fully
      // covered "computed" month.
      _partial: m.source === 'partial',
      _excludedAccounts: m.excluded_accounts || [],
    };
  });

  // The forecast continuation anchors off the latest REAL (non-null) point,
  // never the last array entry -- calendar-year mode's trailing null future
  // placeholders are not a valid anchor.
  var latest = null;
  for (var i = actualRows.length - 1; i >= 0; i--) {
    if (actualRows[i].net != null) { latest = actualRows[i]; break; }
  }
  var nonCashNet = latest ? latest.net - currentCash : 0;
  var nonCashCurrentNet = latest && latest.currentNet != null ? latest.currentNet - currentCash : 0;

  var indexByMonthKey = {};
  actualRows.forEach(function (row, idx) { indexByMonthKey[row._monthKey] = idx; });

  var rows = actualRows.slice();
  var appended = [];
  (input.cashForecastRows || [])
    .filter(function (r) { return r._isForecast; })
    .forEach(function (r) {
      var forecastTotal = r._forecastTotal || 0;
      var net = nonCashNet + forecastTotal;
      // Only cash (part of current net) has a forward-looking source;
      // fixed net holds flat at its latest actual value.
      var currentNet = nonCashCurrentNet + forecastTotal;
      var fixedNet = latest ? latest.fixedNet : null;
      var fields = { net: net, currentNet: currentNet, fixedNet: fixedNet, _isForecast: true };
      if (Object.prototype.hasOwnProperty.call(indexByMonthKey, r._monthKey)) {
        var idx = indexByMonthKey[r._monthKey];
        rows[idx] = Object.assign({}, rows[idx], fields);
      } else {
        appended.push(Object.assign({ month: r.month, _monthKey: r._monthKey }, fields));
      }
    });

  return rows.concat(appended);
}

/**
 * Sum multiple net-assets-series responses' `series` arrays by month key
 * (e.g. business + personal, for the group scope -- the same "sum two
 * scoped balance sheets" convention groupStatCards.js's Net Assets card
 * already uses, just per-month instead of a single current total). Sums
 * fixed_net/current_net together with net so the combined series still
 * carries the fixed/current split, same raw-series shape as a single
 * scope's response.
 *
 * A month renders as soon as ANY input series has real coverage for it --
 * same "any account covers it" rule services/net_assets_series.py applies
 * WITHIN a single scope (a month there is only "no_coverage" when NONE of
 * that scope's live accounts have a snapshot), extended here to the scopes
 * themselves: an input series with no coverage for a month (e.g. Personal
 * accounts that only started reporting in May) contributes nothing to that
 * month's sum rather than blanking a sibling scope's real figure (e.g.
 * Business, covered since February). Only when EVERY input has no coverage
 * for a month is the combined figure genuinely unknown and stays null.
 *
 * Partial cash coverage (source="partial", services/net_assets_series.py)
 * on any input series makes the COMBINED month partial too -- the group
 * total is only as complete as its most understated component, so summing
 * away a scope's own partial flag would hide exactly the gap this feature
 * exists to surface. A whole input scope missing for the month (net=null)
 * also makes the combined month partial, even when that scope's own
 * response carries no excluded-account names to union (a "no_coverage"
 * response never lists them -- see net_assets_series.py's own per-scope
 * "no_coverage" vs "partial" distinction). Excluded account labels from
 * every partial input are unioned, deduped, never fabricated for a scope
 * that didn't name any.
 */
export function sumNetAssetsSeries(seriesList) {
  var lists = (seriesList || []).map(function (series) { return series || []; });

  // Index each input by month key, and separately collect the union of
  // every month key seen across all inputs -- a month key that one input
  // simply never emits (e.g. a trailing-window series cropped to start
  // later than a sibling scope's) must be treated the same as that input
  // explicitly reporting net=null for it, never silently skipped over.
  var pointsByListAndMonth = lists.map(function (series) {
    var byMonth = {};
    series.forEach(function (m) { byMonth[m.month] = m; });
    return byMonth;
  });
  var order = [];
  var seenMonths = {};
  lists.forEach(function (series) {
    series.forEach(function (m) {
      if (!Object.prototype.hasOwnProperty.call(seenMonths, m.month)) {
        seenMonths[m.month] = true;
        order.push(m.month);
      }
    });
  });
  order.sort();

  return order.map(function (mk) {
    var net = 0, fixed_net = 0, current_net = 0, covered = 0, partial = false;
    var excludedAccounts = [];
    pointsByListAndMonth.forEach(function (byMonth) {
      var m = byMonth[mk];
      if (!m || m.net == null) {
        // This scope has no coverage at all for this month (whether it
        // reported an explicit null point or never mentioned the month) --
        // contributes nothing, but (unlike the old intersection rule) does
        // not blank a sibling scope's real figure for the same month.
        partial = true;
        return;
      }
      covered += 1;
      net += parseFloat(m.net) || 0;
      fixed_net += parseFloat(m.fixed_net) || 0;
      current_net += parseFloat(m.current_net) || 0;
      if (m.source === 'partial' && (m.excluded_accounts || []).length) {
        partial = true;
        m.excluded_accounts.forEach(function (label) {
          if (excludedAccounts.indexOf(label) === -1) excludedAccounts.push(label);
        });
      }
    });
    if (covered === 0) {
      return { month: mk, net: null, fixed_net: null, current_net: null, source: 'computed', excluded_accounts: [] };
    }
    return {
      month: mk, net: net, fixed_net: fixed_net, current_net: current_net,
      source: partial ? 'partial' : 'computed', excluded_accounts: excludedAccounts,
    };
  });
}

export var NET_ASSETS_BASIS_CAPTION =
  'Historical months: cash reconstructed from transactions, fixed assets/liabilities from their own dated valuations, ' +
  'receivables and intercompany held flat at today’s value (no dated history). ' +
  'Forecast months: non-cash held flat, cash rolls forward from confirmed bookings only.';
